import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// Relative imports (not the app's `@/` alias) on purpose: the shared
// frontend jest config maps `@/` to the dashboard app, so alias imports
// make super-admin modules untestable. Contained to the TELV1-503 files.
import { translationsApi } from '../../api/translations';
import { useToast } from '../../components/ui/use-toast';

/**
 * Query key factory for translation coverage (TELV1-503).
 */
export const translationKeys = {
  all: ['translations'] as const,
  summary: (projectId: string) => [...translationKeys.all, projectId, 'summary'] as const,
};

/**
 * Per-language translation coverage for a project.
 *
 * Polling design (TELV1-503 architect review F1/F2 — deliberate, please
 * don't "simplify" to a fixed 5s):
 * - The analyzer's summary endpoint is throttled at 20/min per
 *   (source IP, handler), and every super-admin viewer reaches it through
 *   the one super-admin container IP — the bucket is SHARED across all
 *   admins, tabs and projects. It is also an uncached scan of all the
 *   project's digested conversations. A 5s poll from two viewers would
 *   429 and hammer Mongo.
 * - So: 30s when idle, 8s only while a forced retry is being watched.
 *   The interval function always returns a positive number — the
 *   conditional-interval freeze trap documented in useProjects.ts only
 *   applies to intervals that return `false` and never re-arm.
 * - Mount this hook inside the card that renders on the active tab only
 *   (Radix unmounts inactive TabsContent), so backgrounded tabs of the
 *   admin panel don't keep polling.
 */
export function useTranslationSummary(projectId: string, isRetryWatchActive: boolean) {
  return useQuery({
    queryKey: translationKeys.summary(projectId),
    queryFn: () => translationsApi.getSummary(projectId),
    enabled: !!projectId,
    refetchInterval: () => (isRetryWatchActive ? 8000 : 30000),
    // Backgrounded tabs stop polling (PR review): the retry keeps running
    // server-side either way, refetchOnWindowFocus catches the admin up on
    // return, and an idle hidden tab shouldn't spend the shared 20/min
    // throttle bucket. (The analysis-progress hook differs deliberately —
    // its endpoint is cached + 100/min.)
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
    // No request retries: a transient failure just waits for the next
    // poll tick, which is the retry. Mirrors useAnalysisRunProgress.
    retry: 0,
  });
}

interface RetryMissingVariables {
  projectId: string;
  language: string;
}

/**
 * Forced retry of a language's missing translations. Non-destructive by
 * contract (never re-translates existing documents). Completion is
 * observed via the summary poll — the POST returns as soon as the work
 * is queued.
 */
export function useRetryMissingTranslations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ projectId, language }: RetryMissingVariables) =>
      translationsApi.retryMissing(projectId, language),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: translationKeys.summary(variables.projectId),
      });
      toast({
        title: 'Retry started',
        description: `Missing ${variables.language} translations were re-queued. Progress updates below as conversations complete.`,
      });
    },
    onError: (error: unknown, variables) => {
      toast({
        title: 'Retry failed to start',
        description:
          error instanceof Error
            ? error.message
            : `Could not queue the ${variables.language} retry. Try again in a minute (the endpoint is rate-limited).`,
        variant: 'destructive',
      });
    },
  });
}
